// src/store/modules/alumno.js
import axios from "axios";

const isFile = (value) => typeof File !== "undefined" && value instanceof File;

const appendFormValue = (formData, key, value) => {
  if (value === undefined) return;
  if (value === null) {
    formData.append(key, "");
    return;
  }

  formData.append(key, value);
};

const buildAlumnoFormData = (alumno, includeMethodOverride = false) => {
  const formData = new FormData();

  if (includeMethodOverride) {
    formData.append("_method", "PUT");
  }

  Object.entries(alumno).forEach(([key, value]) => {
    if (["id", "fotoPreview", "genero", "matriculas", "alquileres"].includes(key)) return;
    if (key === "foto" && !isFile(value)) return;

    appendFormValue(formData, key, value);
  });

  return formData;
};

const state = () => ({
  alumnos: [], // lista completa con sus relaciones
  generos: [],
});

/* ----------------------  MUTATIONS  ---------------------- */
const mutations = {
  /* ← catálogos básicos ---------------------------------- */
  SET_ALUMNOS(state, alumnos) {
    state.alumnos = alumnos;
  },
  ADD_ALUMNO(state, alumno) {
    state.alumnos.push(alumno);
  },
  UPDATE_ALUMNO(state, alumno) {
    const i = state.alumnos.findIndex((a) => a.id === alumno.id);
    if (i !== -1) state.alumnos.splice(i, 1, alumno);
  },
  DELETE_ALUMNO(state, id) {
    state.alumnos = state.alumnos.filter((a) => a.id !== id);
  },
  SET_GENEROS(state, generos) {
    state.generos = generos;
  },

  /* ← NUEVO: cuando el backend crea una matrícula ---------- */
  ADD_MATRICULA(state, { alumnoId, matricula }) {
    const alumno = state.alumnos.find((a) => a.id === alumnoId);
    if (alumno) {
      if (!Array.isArray(alumno.matriculas)) alumno.matriculas = [];
      alumno.matriculas.push(matricula);
    }
  },
  UPDATE_MATRICULA(state, { alumnoId, matricula }) {
    const alumno = state.alumnos.find((a) => a.id === alumnoId);
    if (alumno && Array.isArray(alumno.matriculas)) {
      const idx = alumno.matriculas.findIndex((m) => m.id === matricula.id);
      if (idx !== -1) alumno.matriculas.splice(idx, 1, matricula);
    }
  },
  DELETE_MATRICULA(state, { alumnoId, matriculaId }) {
    const alumno = state.alumnos.find((a) => a.id === alumnoId);
    if (alumno && Array.isArray(alumno.matriculas)) {
      alumno.matriculas = alumno.matriculas.filter(
        (m) => m.id !== matriculaId
      );
    }
  },
};

/* ------------------------  ACTIONS  ---------------------- */
const actions = {
  /* catálogos */
  async fetchAlumnos({ commit }, params = {}) {
    const config = { params };
    const { data } = await axios.get("/api/alumno", config);
    const alumnos = Array.isArray(data) ? data : data.data;
    commit("SET_ALUMNOS", alumnos);
    return data;
  },

  async fetchAlumnoById({ commit }, id) {
    const { data } = await axios.get(`/api/alumno/${id}`);
    // guarda/actualiza en el store para que sea reactivo
    commit("UPDATE_ALUMNO", data);
    return data;
  },

  async createAlumno({ commit }, alumno) {
    const payload = isFile(alumno?.foto) ? buildAlumnoFormData(alumno) : alumno;
    const config =
      payload instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined;

    const { data } = await axios.post("/api/alumno", payload, config);
    commit("ADD_ALUMNO", data);
    return data;
  },

  async saveAlumno({ dispatch }, alumno) {
    // delega en createAlumno / updateAlumno según corresponda
    if (alumno.id) {
      const hasFoto = isFile(alumno?.foto);
      const payload = hasFoto ? buildAlumnoFormData(alumno, true) : alumno;
      const config = hasFoto
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : undefined;
      const request = hasFoto
        ? axios.post(`/api/alumno/${alumno.id}`, payload, config)
        : axios.put(`/api/alumno/${alumno.id}`, payload);
      const { data } = await request;
      // usa la mutación existente
      dispatch("fetchAlumnoById", alumno.id); // refresca por si vinieron relaciones
      return data;
    }
    return dispatch("createAlumno", alumno);
  },

  async deleteAlumno({ commit }, alumno) {
    await axios.delete(`/api/alumno/${alumno.id}`);
    commit("DELETE_ALUMNO", alumno.id);
  },

  async deleteAlumnoFoto({ dispatch }, alumnoId) {
    await axios.delete(`/api/alumno/${alumnoId}/foto`);
    return dispatch("fetchAlumnoById", alumnoId);
  },

  /* auxiliares */
  async buscarAlumnoPorDni(_, dni) {
    const { data } = await axios.post("/api/alumno/buscar-dni", { dni });
    return data;
  },

  async fetchGeneros({ commit }) {
    const { data } = await axios.get("/api/genero");
    commit("SET_GENEROS", data);
  },

  async fetchMediosContacto() {
    const { data } = await axios.get("/api/medios-contacto");
    return data;
  },

  /* matrícula */
  async matricularAlumnoCurso(
    { commit },
    { alumno_id, curso_id, precio_curso_id, cantidad, descuento, visa, fecha, medio_contacto_id }
  ) {
    // El costo lo calcula el backend a partir del paquete, la cantidad, el descuento y el POS
    const { data } = await axios.post("/api/alumno/matricular", {
      alumno_id,
      curso_id,
      precio_curso_id,
      cantidad,
      descuento,
      visa,
      fecha,
      medio_contacto_id,
    });

    /* 👉 notifica al store: ahora el alumno tiene una matrícula más */
    commit("ADD_MATRICULA", { alumnoId: alumno_id, matricula: data });

    return data; // para que el componente muestre feedback si quiere
  },

  // Obtener una matrícula por ID
  async fetchMatriculaById(_, id) {
    const { data } = await axios.get(`/api/matricula/${id}`);
    return data;
  },

  // Guardar la programación de clases
  async guardarProgramacionClases(_, { matriculaId, clases, circuito = false }) {
    const { data } = await axios.post('/api/programaciones', {
      matricula_id: matriculaId,
      clases,
      circuito,
    });
    return data;
  },

  async deleteMatricula({ commit }, { alumnoId, matriculaId }) {
    await axios.delete(`/api/matricula/${matriculaId}`);
    commit('DELETE_MATRICULA', { alumnoId, matriculaId });
  },

  async updateMatricula(
    { commit },
    { alumnoId, matriculaId, fecha, costo, visa, medio_contacto_id }
  ) {
    const { data } = await axios.put(`/api/matricula/${matriculaId}`, {
      fecha,
      costo,
      visa,
      medio_contacto_id,
    });
    commit('UPDATE_MATRICULA', { alumnoId, matricula: data });
    return data;
  },

  // Obtener todas las programaciones registradas
  async fetchProgramaciones(_, params = {}) {
    const { data } = await axios.get('/api/programaciones', { params });
    return data;
  },

  async registrarPagoMatricula(_, { matriculaId, monto, fecha }) {
    const { data } = await axios.post('/api/pago-matricula', {
      matricula_id: matriculaId,
      monto,
      fecha,
    });
    return data;
  },

  async eliminarPagoMatricula(_, pagoMatriculaId) {
    await axios.delete(`/api/pago-matricula/${pagoMatriculaId}`);
  },
};

/* ------------------------  GETTERS  ---------------------- */
const getters = {
  alumnos: (state) => state.alumnos,
  generos: (state) => state.generos,
};

/* ---------------------  EXPORTACIÓN  --------------------- */
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
